import { useState, useEffect, useCallback, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FreetextSuggestion, ResolveFreetextInput } from '@/types/admin'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Label } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { Separator } from '@brent/core-ui/ui'
import { Textarea } from '@brent/core-ui/ui'
import {
  Search,
  ArrowLeft,
  ArrowRight,
  Check,
  Plus,
  Loader2,
  User,
  Music,
  ExternalLink,
} from 'lucide-react'
import { useComposerSearch, useWorkSearch } from '@/hooks/useAdminSearch'
import { createCanonicalComposer } from '@/services/api/canonical-composers'
import type { CreateCanonicalComposerInput } from '@/types/canonical-composer'
import { useLinkSuggestion } from '@/hooks/useCanonicalPieces'
import { useToast } from '@/hooks/use-toast'

// --- Types ---

type ResolveStep = 'composer' | 'piece' | 'confirm'

interface SelectedComposer {
  type: 'wikidata' | 'canonical' | 'new_canonical'
  wikidata_id?: string
  canonical_composer_id?: string
  name: string
  birth_year?: number | null
  death_year?: number | null
  notes?: string | null
}

interface SelectedPiece {
  type: 'wikidata' | 'canonical' | 'new_canonical'
  wikidata_id?: string
  canonical_piece_id?: string
  title: string
}

interface ResolveFreetextDialogProps {
  suggestion: FreetextSuggestion | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onResolve: (suggestionId: string, input: ResolveFreetextInput) => void
  isResolving: boolean
}

// --- Helpers ---

function formatYears(birth: number | null | undefined, death: number | null | undefined): string {
  if (birth == null && death == null) return ''
  const b = birth != null ? String(birth) : '?'
  const d = death != null ? String(death) : 'present'
  return `(${b}–${d})`
}

function composerTypeBadge(type: 'wikidata' | 'canonical' | 'new_canonical') {
  switch (type) {
    case 'wikidata':
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
          Wikidata
        </Badge>
      )
    case 'canonical':
      return (
        <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200">
          Canonical
        </Badge>
      )
    case 'new_canonical':
      return (
        <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200">
          New Canonical
        </Badge>
      )
  }
}

function pieceTypeBadge(type: 'wikidata' | 'canonical' | 'new_canonical') {
  switch (type) {
    case 'wikidata':
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
          Wikidata
        </Badge>
      )
    case 'canonical':
      return (
        <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200">
          Canonical
        </Badge>
      )
    case 'new_canonical':
      return (
        <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200">
          New
        </Badge>
      )
  }
}

// --- Hook: debounced value ---

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

// --- Main Component ---

export function ResolveFreetextDialog({
  suggestion,
  open,
  onOpenChange,
  onResolve,
  isResolving,
}: ResolveFreetextDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const linkMutation = useLinkSuggestion()

  // --- Step state ---
  const [step, setStep] = useState<ResolveStep>('composer')
  const [selectedComposer, setSelectedComposer] = useState<SelectedComposer | null>(null)
  const [selectedPiece, setSelectedPiece] = useState<SelectedPiece | null>(null)

  // --- Search state ---
  const [composerSearchInput, setComposerSearchInput] = useState('')
  const [pieceSearchInput, setPieceSearchInput] = useState('')

  // --- Create canonical composer form ---
  const [showCreateComposer, setShowCreateComposer] = useState(false)
  const [newComposerName, setNewComposerName] = useState('')
  const [newComposerBirthYear, setNewComposerBirthYear] = useState('')
  const [newComposerDeathYear, setNewComposerDeathYear] = useState('')
  const [newComposerNotes, setNewComposerNotes] = useState('')

  // --- Create canonical piece form ---
  const [showCreatePiece, setShowCreatePiece] = useState(false)
  const [newPieceTitle, setNewPieceTitle] = useState('')

  // --- Debounced search queries ---
  const debouncedComposerQuery = useDebouncedValue(composerSearchInput, 300)
  const debouncedPieceQuery = useDebouncedValue(pieceSearchInput, 300)

  // --- Determine composer ID and type for work search ---
  const composerId = selectedComposer?.wikidata_id || selectedComposer?.canonical_composer_id || ''
  const composerType: 'wikidata' | 'canonical' =
    selectedComposer?.type === 'wikidata' ? 'wikidata' : 'canonical'

  // --- Search hooks ---
  const composerSearch = useComposerSearch(debouncedComposerQuery)
  const workSearch = useWorkSearch(debouncedPieceQuery, composerId, composerType)

  // --- Create canonical composer mutation ---
  const createComposerMutation = useMutation({
    mutationFn: (input: CreateCanonicalComposerInput) => createCanonicalComposer(input),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['admin-search'] })
      const composer: SelectedComposer = {
        type: 'canonical',
        canonical_composer_id: created.canonical_composer_id,
        name: created.name,
        birth_year: created.birth_year,
        death_year: created.death_year,
        notes: created.notes,
      }
      setSelectedComposer(composer)
      setShowCreateComposer(false)
      advanceFromComposer(composer)
      toast({
        title: 'Composer created',
        description: `Canonical composer "${created.name}" created successfully`,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to create canonical composer',
        variant: 'destructive',
      })
    },
  })

  // --- Determine initial step based on suggestion ---
  const determineInitialStep = useCallback((s: FreetextSuggestion): ResolveStep => {
    if (s.is_composer_freetext) return 'composer'
    if (s.is_work_freetext) return 'piece'
    return 'confirm'
  }, [])

  // --- Compute total steps ---
  const totalSteps = useMemo(() => {
    if (!suggestion) return 3
    const hasComposerStep = suggestion.is_composer_freetext
    const hasPieceStep = suggestion.is_work_freetext
    return (hasComposerStep ? 1 : 0) + (hasPieceStep ? 1 : 0) + 1 // +1 for confirm
  }, [suggestion])

  const currentStepNumber = useMemo(() => {
    if (!suggestion) return 1
    if (step === 'composer') return 1
    if (step === 'piece') return suggestion.is_composer_freetext ? 2 : 1
    // confirm
    return totalSteps
  }, [suggestion, step, totalSteps])

  // --- Reset state when suggestion changes or dialog opens ---
  useEffect(() => {
    if (open && suggestion) {
      const initialStep = determineInitialStep(suggestion)
      setStep(initialStep)
      setSelectedPiece(null)
      setShowCreateComposer(false)
      setShowCreatePiece(false)
      setNewComposerName(suggestion.original_composer_text || suggestion.composer || '')
      setNewComposerBirthYear('')
      setNewComposerDeathYear('')
      setNewComposerNotes('')
      setNewPieceTitle(suggestion.original_work_text || suggestion.title || '')
      setComposerSearchInput(suggestion.original_composer_text || suggestion.composer || '')
      setPieceSearchInput(suggestion.original_work_text || suggestion.title || '')

      // If composer is already resolved (only work is freetext), pre-populate composer
      if (!suggestion.is_composer_freetext && suggestion.is_work_freetext) {
        if (suggestion.composer_wikidata_id) {
          setSelectedComposer({
            type: 'wikidata',
            wikidata_id: suggestion.composer_wikidata_id,
            name: suggestion.composer,
          })
        } else if (suggestion.canonical_composer_id) {
          setSelectedComposer({
            type: 'canonical',
            canonical_composer_id: suggestion.canonical_composer_id,
            name: suggestion.composer,
          })
        } else {
          setSelectedComposer(null)
        }
      } else {
        setSelectedComposer(null)
      }
    }
  }, [open, suggestion, determineInitialStep])

  // --- Navigation ---
  const advanceFromComposer = useCallback(
    (_composer: SelectedComposer) => {
      if (suggestion?.is_work_freetext) {
        setStep('piece')
        setPieceSearchInput(suggestion.original_work_text || suggestion.title || '')
        setShowCreatePiece(false)
        setSelectedPiece(null)
        // Reset new piece title for the form
        setNewPieceTitle(suggestion.original_work_text || suggestion.title || '')
      } else {
        // No work freetext — go straight to confirm with no piece selection needed
        setSelectedPiece(null)
        setStep('confirm')
      }
    },
    [suggestion],
  )

  const handleSelectComposer = useCallback(
    (composer: SelectedComposer) => {
      setSelectedComposer(composer)
      advanceFromComposer(composer)
    },
    [advanceFromComposer],
  )

  const handleSelectPiece = useCallback((piece: SelectedPiece) => {
    setSelectedPiece(piece)
    setStep('confirm')
  }, [])

  const handleBack = useCallback(() => {
    if (step === 'confirm') {
      if (suggestion?.is_work_freetext) {
        setStep('piece')
      } else if (suggestion?.is_composer_freetext) {
        setStep('composer')
      }
    } else if (step === 'piece' && suggestion?.is_composer_freetext) {
      setStep('composer')
    }
  }, [step, suggestion])

  // --- Create canonical composer handler ---
  const handleCreateComposer = useCallback(() => {
    if (!newComposerName.trim()) {
      toast({
        title: 'Error',
        description: 'Composer name is required',
        variant: 'destructive',
      })
      return
    }
    const input: CreateCanonicalComposerInput = {
      name: newComposerName.trim(),
      birth_year: newComposerBirthYear ? parseInt(newComposerBirthYear, 10) : null,
      death_year: newComposerDeathYear ? parseInt(newComposerDeathYear, 10) : null,
      notes: newComposerNotes.trim() || null,
    }
    createComposerMutation.mutate(input)
  }, [
    newComposerName,
    newComposerBirthYear,
    newComposerDeathYear,
    newComposerNotes,
    createComposerMutation,
    toast,
  ])

  // --- Create new canonical piece (store for confirm step) ---
  const handleCreatePiece = useCallback(() => {
    if (!newPieceTitle.trim()) {
      toast({
        title: 'Error',
        description: 'Work title is required',
        variant: 'destructive',
      })
      return
    }
    const piece: SelectedPiece = {
      type: 'new_canonical',
      title: newPieceTitle.trim(),
    }
    setSelectedPiece(piece)
    setStep('confirm')
  }, [newPieceTitle, toast])

  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  // --- Final resolve handler ---
  const handleResolve = useCallback(() => {
    if (!suggestion) return

    // If linking to an existing canonical piece, use linkSuggestionToCanonicalPiece
    if (selectedPiece?.type === 'canonical' && selectedPiece.canonical_piece_id) {
      linkMutation.mutate(
        {
          canonicalPieceId: selectedPiece.canonical_piece_id,
          suggestionId: suggestion.suggestion_id,
        },
        {
          onSuccess: () => {
            toast({
              title: 'Success',
              description: 'Suggestion linked to canonical piece successfully',
            })
            handleClose()
          },
          onError: (error: Error) => {
            toast({
              title: 'Error',
              description: error?.message || 'Failed to link suggestion',
              variant: 'destructive',
            })
          },
        },
      )
      return
    }

    // Otherwise, use the resolve endpoint with IDs
    const input: ResolveFreetextInput = {}

    if (selectedComposer) {
      if (selectedComposer.type === 'wikidata' && selectedComposer.wikidata_id) {
        input.composer_wikidata_id = selectedComposer.wikidata_id
      } else if (
        (selectedComposer.type === 'canonical' || selectedComposer.type === 'new_canonical') &&
        selectedComposer.canonical_composer_id
      ) {
        input.canonical_composer_id = selectedComposer.canonical_composer_id
      }
    }

    if (selectedPiece) {
      if (selectedPiece.type === 'wikidata' && selectedPiece.wikidata_id) {
        input.work_wikidata_id = selectedPiece.wikidata_id
      }
      // For new_canonical pieces, the backend creates the canonical piece
      // when only a composer ID is provided (no work_wikidata_id)
    }

    onResolve(suggestion.suggestion_id, input)
  }, [suggestion, selectedComposer, selectedPiece, onResolve, linkMutation, toast, handleClose])

  const isBusy = isResolving || linkMutation.isPending || createComposerMutation.isPending

  if (!suggestion) return null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Resolve Freetext Entry</DialogTitle>
          <DialogDescription>
            Step {currentStepNumber} of {totalSteps} — {stepLabel(step)}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {step === 'composer' && (
            <ComposerStep
              suggestion={suggestion}
              searchInput={composerSearchInput}
              onSearchInputChange={setComposerSearchInput}
              searchResults={composerSearch}
              debouncedQuery={debouncedComposerQuery}
              onSelect={handleSelectComposer}
              showCreateForm={showCreateComposer}
              onToggleCreateForm={setShowCreateComposer}
              newComposerName={newComposerName}
              onNewComposerNameChange={setNewComposerName}
              newComposerBirthYear={newComposerBirthYear}
              onNewComposerBirthYearChange={setNewComposerBirthYear}
              newComposerDeathYear={newComposerDeathYear}
              onNewComposerDeathYearChange={setNewComposerDeathYear}
              newComposerNotes={newComposerNotes}
              onNewComposerNotesChange={setNewComposerNotes}
              onCreateComposer={handleCreateComposer}
              isCreating={createComposerMutation.isPending}
            />
          )}

          {step === 'piece' && selectedComposer && (
            <PieceStep
              suggestion={suggestion}
              selectedComposer={selectedComposer}
              searchInput={pieceSearchInput}
              onSearchInputChange={setPieceSearchInput}
              searchResults={workSearch}
              debouncedQuery={debouncedPieceQuery}
              onSelect={handleSelectPiece}
              showCreateForm={showCreatePiece}
              onToggleCreateForm={setShowCreatePiece}
              newPieceTitle={newPieceTitle}
              onNewPieceTitleChange={setNewPieceTitle}
              onCreatePiece={handleCreatePiece}
            />
          )}

          {step === 'confirm' && (
            <ConfirmStep
              suggestion={suggestion}
              selectedComposer={selectedComposer}
              selectedPiece={selectedPiece}
            />
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <div>
            {step !== determineInitialStep(suggestion) && (
              <Button variant="outline" onClick={handleBack} disabled={isBusy}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={isBusy}>
              Cancel
            </Button>
            {step === 'confirm' && (
              <Button onClick={handleResolve} disabled={isBusy}>
                {isBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resolving...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Resolve
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Step label helper ---

function stepLabel(step: ResolveStep): string {
  switch (step) {
    case 'composer':
      return 'Resolve Composer'
    case 'piece':
      return 'Resolve Piece'
    case 'confirm':
      return 'Confirm Resolution'
  }
}

// --- Composer Step ---

interface ComposerStepProps {
  suggestion: FreetextSuggestion
  searchInput: string
  onSearchInputChange: (value: string) => void
  searchResults: ReturnType<typeof useComposerSearch>
  debouncedQuery: string
  onSelect: (composer: SelectedComposer) => void
  showCreateForm: boolean
  onToggleCreateForm: (show: boolean) => void
  newComposerName: string
  onNewComposerNameChange: (value: string) => void
  newComposerBirthYear: string
  onNewComposerBirthYearChange: (value: string) => void
  newComposerDeathYear: string
  onNewComposerDeathYearChange: (value: string) => void
  newComposerNotes: string
  onNewComposerNotesChange: (value: string) => void
  onCreateComposer: () => void
  isCreating: boolean
}

function ComposerStep({
  suggestion,
  searchInput,
  onSearchInputChange,
  searchResults,
  debouncedQuery,
  onSelect,
  showCreateForm,
  onToggleCreateForm,
  newComposerName,
  onNewComposerNameChange,
  newComposerBirthYear,
  onNewComposerBirthYearChange,
  newComposerDeathYear,
  onNewComposerDeathYearChange,
  newComposerNotes,
  onNewComposerNotesChange,
  onCreateComposer,
  isCreating,
}: ComposerStepProps) {
  const { data, isLoading, isError } = searchResults
  const hasQuery = debouncedQuery.length >= 2
  const hasResults =
    data && (data.wikidata_composers.length > 0 || data.canonical_composers.length > 0)

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Original entry:</span>{' '}
        {suggestion.original_composer_text || suggestion.composer}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search for a composer..."
          value={searchInput}
          onChange={(e) => onSearchInputChange(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="max-h-[300px] overflow-y-auto space-y-4">
        {isLoading && hasQuery && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
          </div>
        )}

        {isError && hasQuery && (
          <div className="text-sm text-destructive py-4 text-center">
            Failed to search composers. Please try again.
          </div>
        )}

        {hasQuery && !isLoading && !isError && !hasResults && (
          <div className="text-sm text-muted-foreground py-4 text-center">No results found</div>
        )}

        {data && data.wikidata_composers.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Wikidata Composers
            </div>
            <div className="space-y-1">
              {data.wikidata_composers.map((c) => (
                <button
                  key={c.wikidata_id}
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center justify-between group"
                  onClick={() =>
                    onSelect({
                      type: 'wikidata',
                      wikidata_id: c.wikidata_id,
                      name: c.name,
                      birth_year: c.birth_year,
                      death_year: c.death_year,
                    })
                  }
                >
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{c.name}</span>
                    {(c.birth_year != null || c.death_year != null) && (
                      <span className="text-sm text-muted-foreground">
                        {formatYears(c.birth_year, c.death_year)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                      {c.wikidata_id}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {data && data.canonical_composers.length > 0 && (
          <div>
            {data.wikidata_composers.length > 0 && <Separator className="my-2" />}
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Canonical Composers
            </div>
            <div className="space-y-1">
              {data.canonical_composers.map((c) => (
                <button
                  key={c.canonical_composer_id}
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center justify-between group"
                  onClick={() =>
                    onSelect({
                      type: 'canonical',
                      canonical_composer_id: c.canonical_composer_id,
                      name: c.name,
                      birth_year: c.birth_year,
                      death_year: c.death_year,
                      notes: c.notes,
                    })
                  }
                >
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{c.name}</span>
                    {(c.birth_year != null || c.death_year != null) && (
                      <span className="text-sm text-muted-foreground">
                        {formatYears(c.birth_year, c.death_year)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="bg-purple-50 text-purple-800 border-purple-200"
                    >
                      {c.piece_count} piece{c.piece_count !== 1 ? 's' : ''}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="bg-purple-50 text-purple-800 border-purple-200"
                    >
                      Canonical
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Separator />

      {!showCreateForm ? (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => onToggleCreateForm(true)}
          disabled={isCreating}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create new canonical composer
        </Button>
      ) : (
        <div className="space-y-3 rounded-md border p-4">
          <div className="text-sm font-medium">Create New Canonical Composer</div>
          <div className="space-y-2">
            <Label htmlFor="new-composer-name">Name</Label>
            <Input
              id="new-composer-name"
              value={newComposerName}
              onChange={(e) => onNewComposerNameChange(e.target.value)}
              placeholder="Composer name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="new-composer-birth">Birth Year</Label>
              <Input
                id="new-composer-birth"
                type="number"
                value={newComposerBirthYear}
                onChange={(e) => onNewComposerBirthYearChange(e.target.value)}
                placeholder="e.g. 1756"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-composer-death">Death Year</Label>
              <Input
                id="new-composer-death"
                type="number"
                value={newComposerDeathYear}
                onChange={(e) => onNewComposerDeathYearChange(e.target.value)}
                placeholder="e.g. 1791"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-composer-notes">Notes</Label>
            <Textarea
              id="new-composer-notes"
              value={newComposerNotes}
              onChange={(e) => onNewComposerNotesChange(e.target.value)}
              placeholder="Optional notes about this composer"
              rows={2}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleCreateForm(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={onCreateComposer} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create & Select
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Piece Step ---

interface PieceStepProps {
  suggestion: FreetextSuggestion
  selectedComposer: SelectedComposer
  searchInput: string
  onSearchInputChange: (value: string) => void
  searchResults: ReturnType<typeof useWorkSearch>
  debouncedQuery: string
  onSelect: (piece: SelectedPiece) => void
  showCreateForm: boolean
  onToggleCreateForm: (show: boolean) => void
  newPieceTitle: string
  onNewPieceTitleChange: (value: string) => void
  onCreatePiece: () => void
}

function PieceStep({
  suggestion,
  selectedComposer,
  searchInput,
  onSearchInputChange,
  searchResults,
  debouncedQuery,
  onSelect,
  showCreateForm,
  onToggleCreateForm,
  newPieceTitle,
  onNewPieceTitleChange,
  onCreatePiece,
}: PieceStepProps) {
  const { data, isLoading, isError } = searchResults
  const hasQuery = debouncedQuery.length >= 2
  const hasResults = data && (data.wikidata_works.length > 0 || data.canonical_pieces.length > 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Composer:</span>
        <span className="font-medium">{selectedComposer.name}</span>
        {composerTypeBadge(selectedComposer.type)}
      </div>

      <div className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Original entry:</span>{' '}
        {suggestion.original_work_text || suggestion.title}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search for a work..."
          value={searchInput}
          onChange={(e) => onSearchInputChange(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="max-h-[300px] overflow-y-auto space-y-4">
        {isLoading && hasQuery && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
          </div>
        )}

        {isError && hasQuery && (
          <div className="text-sm text-destructive py-4 text-center">
            Failed to search works. Please try again.
          </div>
        )}

        {hasQuery && !isLoading && !isError && !hasResults && (
          <div className="text-sm text-muted-foreground py-4 text-center">No results found</div>
        )}

        {data && data.wikidata_works.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Wikidata Works
            </div>
            <div className="space-y-1">
              {data.wikidata_works.map((w) => (
                <button
                  key={w.wikidata_id}
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center justify-between group"
                  onClick={() =>
                    onSelect({
                      type: 'wikidata',
                      wikidata_id: w.wikidata_id,
                      title: w.title,
                    })
                  }
                >
                  <div className="flex items-center gap-2">
                    <Music className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{w.title}</span>
                    {w.catalog_number && (
                      <span className="text-sm text-muted-foreground">{w.catalog_number}</span>
                    )}
                    {w.year != null && (
                      <span className="text-sm text-muted-foreground">({w.year})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                      {w.wikidata_id}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {data && data.canonical_pieces.length > 0 && (
          <div>
            {data.wikidata_works.length > 0 && <Separator className="my-2" />}
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Canonical Pieces
            </div>
            <div className="space-y-1">
              {data.canonical_pieces.map((p) => (
                <button
                  key={p.canonical_piece_id}
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center justify-between group"
                  onClick={() =>
                    onSelect({
                      type: 'canonical',
                      canonical_piece_id: p.canonical_piece_id,
                      title: p.work_title || '(Untitled)',
                    })
                  }
                >
                  <div className="flex items-center gap-2">
                    <Music className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{p.work_title || '(Untitled)'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="bg-purple-50 text-purple-800 border-purple-200"
                    >
                      {p.suggestion_count} suggestion{p.suggestion_count !== 1 ? 's' : ''}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="bg-purple-50 text-purple-800 border-purple-200"
                    >
                      Canonical
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Separator />

      {!showCreateForm ? (
        <Button variant="outline" className="w-full" onClick={() => onToggleCreateForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create new canonical piece
        </Button>
      ) : (
        <div className="space-y-3 rounded-md border p-4">
          <div className="text-sm font-medium">Create New Canonical Piece</div>
          <div className="space-y-2">
            <Label htmlFor="new-piece-title">Work Title</Label>
            <Input
              id="new-piece-title"
              value={newPieceTitle}
              onChange={(e) => onNewPieceTitleChange(e.target.value)}
              placeholder="Work title"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => onToggleCreateForm(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={onCreatePiece}>
              <Plus className="mr-2 h-4 w-4" />
              Create & Select
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Confirm Step ---

interface ConfirmStepProps {
  suggestion: FreetextSuggestion
  selectedComposer: SelectedComposer | null
  selectedPiece: SelectedPiece | null
}

function ConfirmStep({ suggestion, selectedComposer, selectedPiece }: ConfirmStepProps) {
  const originalComposer = suggestion.original_composer_text || suggestion.composer
  const originalWork = suggestion.original_work_text || suggestion.title

  return (
    <div className="space-y-4">
      <div className="rounded-md border p-4 space-y-3">
        <div className="text-sm font-medium">Original Entry</div>
        <div className="text-sm text-muted-foreground">
          {originalComposer} — {originalWork}
        </div>
      </div>

      <div className="flex justify-center">
        <ArrowRight className="h-5 w-5 text-muted-foreground rotate-90" />
      </div>

      <div className="rounded-md border p-4 space-y-3 bg-muted/30">
        <div className="text-sm font-medium">Resolved To</div>
        <div className="space-y-2">
          {selectedComposer ? (
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{selectedComposer.name}</span>
              {composerTypeBadge(selectedComposer.type)}
              {selectedComposer.wikidata_id && (
                <a
                  href={`https://www.wikidata.org/wiki/${selectedComposer.wikidata_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              <span>{suggestion.composer}</span>
              <Badge variant="outline">Unchanged</Badge>
            </div>
          )}

          {selectedPiece ? (
            <div className="flex items-center gap-2 text-sm">
              <Music className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{selectedPiece.title}</span>
              {pieceTypeBadge(selectedPiece.type)}
              {selectedPiece.wikidata_id && (
                <a
                  href={`https://www.wikidata.org/wiki/${selectedPiece.wikidata_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ) : suggestion.is_work_freetext ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Music className="h-4 w-4" />
              <span>{suggestion.title}</span>
              <Badge variant="outline">No work specified — canonical piece will be created</Badge>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Music className="h-4 w-4" />
              <span>{suggestion.title}</span>
              <Badge variant="outline">Unchanged</Badge>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
